import { createSlice } from '@reduxjs/toolkit';
import {
  // GET
  fetchBankAccounts,
  fetchCompanyDocuments,
  fetchCompanyEmployees,
  fetchCompanyFundingApplications,
  fetchAdvance,
  fetchUser,
  fetchUserDocuments,
  downloadDocument,

  // UPDATE
  updateCompany,
  updateCompanyAddress,
  updateUser,
  updateUserAddress,
  blockUnblockUser,
  sendIDVerificationEmail,
  updateDocument,

  // Banking
  blockTraderootAccount,
  reassignEmployee,
  unblockTraderootAccount,

  // CREATE
  createUserNew,
  createTraderootMember,
  createBankAccount,
  assignEmployee,
  unassignEmployee,
  createUserAddress,
} from '@features/workspace/store/actions';
import {
  fetchCompanies,
  fetchCompany,
  fetchEmployee,
} from '@features/workspace/store/actions/fetch';
import {
  createCompanyAddress,
  createEmailChangeRequest,
} from '@features/workspace/store/actions/create';
import {
  IBankAccount,
  ICompany,
  IDocument,
  IEmployee,
  IUser,
} from '@features/workspace/types';
import logger from '@app/services/logger';

export interface IWorkspaceState {
  personId: string | null;
  activeCompanyId: string | null;
  companies: ICompany[];
  users: IUser[];
  employees: IEmployee[];
  fundingApplications: any[];
  advances: any[];
  documents: IDocument[];
  notes: any[];
  // Banking
  bankAccounts: Array<IBankAccount>;
  activeBankAccountNumber: null | string;
  isBankAccountsLoading: boolean;
  isCompanyDocumentsLoading: boolean;
  isDocumentsLoading: boolean;
  isUsersLoading: boolean;
  isEmployeesLoading: boolean;
  // External healthcheck
  isTraderootDown: boolean;
}

const initialState: IWorkspaceState = {
  personId: null,
  companies: [],
  activeCompanyId: null,
  users: [],
  fundingApplications: [],
  advances: [],
  employees: [],
  documents: [],
  notes: [],
  // banking
  bankAccounts: [],
  activeBankAccountNumber: null,
  isBankAccountsLoading: true,
  isCompanyDocumentsLoading: true,
  isDocumentsLoading: true,
  isUsersLoading: true,
  isEmployeesLoading: true,
  // External healthcheck
  isTraderootDown: false,
};

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    setPersonId: (state, { payload }) => {
      state.personId = payload;
    },
    setActiveCompanyId: (state, { payload }) => {
      state.activeCompanyId = payload;
    },
    setCompanies: (state, { payload }) => {
      state.companies = payload;
    },
    setUsers: (state, { payload }) => {
      state.users = payload;
    },
    setRemoveUser: (state, { payload }) => {
      state.users = state.users.filter((user) => user.id !== payload);
    },
    setDocumentStatus: (state, { payload }) => {
      const { documentId, status } = payload;

      state.documents = state.documents.map((doc: any) =>
        doc.id !== documentId
          ? doc
          : {
              ...doc,
              zendeskStatus: status,
            }
      );
    },
    setEmployees: (state, { payload }) => {
      state.employees = payload;
    },
    setDocuments: (state, { payload }) => {
      state.documents = payload;
    },
    setNotes: (state, { payload }) => {
      state.notes = payload;
    },
    setFundingApplications: (state, { payload }) => {
      state.fundingApplications = payload;
    },
    setAdvances: (state, { payload }) => {
      state.advances = payload;
    },
    // banking
    setBankAccounts: (state, { payload }) => {
      state.bankAccounts = payload;
    },
    setActiveBankAccountNumber: (state, { payload }) => {
      state.activeBankAccountNumber = payload;
    },
    setIsBankAccountsLoading: (state, { payload }) => {
      state.isBankAccountsLoading = payload;
    },
    setIsCompanyDocumentsLoading: (state, { payload }) => {
      state.isCompanyDocumentsLoading = payload;
    },
    setIsDocumentsLoading: (state, { payload }) => {
      state.isDocumentsLoading = payload;
    },
    setIsUsersLoading: (state, { payload }) => {
      state.isUsersLoading = payload;
    },
    setIsEmployeesLoading: (state, { payload }) => {
      state.isEmployeesLoading = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateDocument.fulfilled, (state, { meta: { arg } }) => {
      logger.debug('updateDocument.fulfilled', arg);
    });
    // COMPANY
    builder.addCase(fetchCompanies.fulfilled, (state, { payload }) => {
      logger.debug('fetchCompanies.fulfilled');
      state.companies = payload;
    });
    builder.addCase(fetchCompany.fulfilled, (state, { payload }) => {
      logger.debug('fetchCompany.fulfilled', payload.id);
      if (state.companies.some((company) => company.id === payload.id)) {
        state.companies = state.companies.map((company) =>
          company.id === payload.id ? payload : company
        );
      } else {
        state.companies = [...state.companies, payload];
      }
    });
    builder.addCase(updateCompany.fulfilled, (state, { payload }) => {
      logger.debug('updateCompany.fulfilled', payload.id);
      if (state.activeCompanyId) {
        state.companies = state.companies.map((company) =>
          state.activeCompanyId === company.id
            ? {
                ...company,
                ...payload,
              }
            : company
        );
      }
    });
    // COMPANY ADDRESS
    builder.addCase(
      createCompanyAddress.fulfilled,
      (state, { payload, meta: { arg } }) => {
        state.companies = state.companies.map((company) =>
          arg.companyId === company.id
            ? {
                ...company,
                address: payload,
              }
            : company
        );
      }
    );
    builder.addCase(
      updateCompanyAddress.fulfilled,
      (state, { payload, meta: { arg } }) => {
        state.companies = state.companies.map((company) =>
          arg.companyId === company.id
            ? {
                ...company,
                address: payload,
              }
            : company
        );
      }
    );
    // USERS
    builder.addCase(fetchUser.fulfilled, (state, { payload }) => {
      logger.debug('fetchUser.fulfilled', payload.id);
      const iUser = state.users.findIndex((user) => user.id === payload.id);

      if (iUser !== -1) {
        state.users[iUser] = payload;
      } else {
        state.users = [...state.users, payload];
      }
    });
    builder.addCase(
      createUserNew.fulfilled,
      (state, { payload, meta: { arg } }) => {
        logger.debug('createUserNew.fulfilled', arg);
      }
    );
    builder.addCase(
      updateUser.fulfilled,
      (state, { payload, meta: { arg } }) => {
        logger.debug('updateUser.fulfilled', arg);
      }
    );
    // USER ADDRESS
    builder.addCase(
      createUserAddress.fulfilled,
      (state, { payload, meta: { arg } }) => {
        state.users = state.users.map((user) =>
          arg.userId === user.id
            ? {
                ...user,
                address: payload,
              }
            : user
        );
      }
    );
    builder.addCase(
      updateUserAddress.fulfilled,
      (state, { payload, meta: { arg } }) => {
        state.users = state.users.map((user) =>
          arg.userId === user.id
            ? {
                ...user,
                address: payload,
              }
            : user
        );
      }
    );
    // EMPLOYEE
    builder.addCase(
      fetchEmployee.fulfilled,
      (state, { payload, meta: { arg } }) => {
        logger.debug('fetchEmployee.fulfilled', arg);
        const iEmp = state.employees.findIndex(
          (emp) => arg.userId === emp.userId && arg.companyId === emp.companyId
        );

        if (iEmp !== -1) {
          state.employees[iEmp] = payload;
        } else {
          state.employees = [...state.employees, payload];
        }
      }
    );
    builder.addCase(assignEmployee.fulfilled, (state, { meta: { arg } }) => {
      logger.debug('assignEmployee.fulfilled', arg);
    });
    builder.addCase(reassignEmployee.fulfilled, (state, { meta: { arg } }) => {
      logger.debug('reassignEmployee.fulfilled', arg);
    });
    builder.addCase(unassignEmployee.fulfilled, (state, { meta: { arg } }) => {
      logger.debug('unassignEmployee.fulfilled', arg);
    });
    builder.addCase(blockUnblockUser.fulfilled, (state, { payload }) => {});
    builder.addCase(
      sendIDVerificationEmail.fulfilled,
      (state, { payload }) => {}
    );

    builder.addCase(fetchBankAccounts.fulfilled, (state, { payload }) => {
      logger.debug('fetchBankAccounts.fulfilled');
      state.bankAccounts = payload;
      // Init active bank account with first acc
      if (state.bankAccounts.length && !state.activeBankAccountNumber) {
        state.activeBankAccountNumber = state.bankAccounts[0].accountNumber;
      }
      state.isBankAccountsLoading = false;
    });
    builder.addCase(fetchBankAccounts.rejected, (state, { payload }) => {
      logger.debug('fetchBankAccounts.rejected');
      state.isTraderootDown = true;
    });
    builder.addCase(
      fetchCompanyFundingApplications.fulfilled,
      (state, { payload }) => {
        state.fundingApplications = payload;
      }
    );
    builder.addCase(fetchAdvance.fulfilled, (state, { payload }) => {
      state.advances.push(payload);
    });
    builder.addCase(fetchCompanyEmployees.fulfilled, (state, { payload }) => {
      state.employees = payload;
      state.users = payload.map((emp: IEmployee) => ({
        id: emp.userId,
        email: emp.email,
        person: emp.person,
        phoneNumber: emp.phoneNumber,
      }));
      state.isEmployeesLoading = false;
    });

    // BANK ACCOUNT
    builder.addCase(
      blockTraderootAccount.fulfilled,
      (state, { meta: { arg }, payload }) => {
        logger.debug('blockTraderootAccount.fulfilled', arg, payload);
      }
    );
    builder.addCase(
      unblockTraderootAccount.fulfilled,
      (state, { meta: { arg }, payload }) => {
        logger.debug('unblockTraderootAccount.fulfilled', arg, payload);
      }
    );
    builder.addCase(fetchCompanyDocuments.fulfilled, (state, { payload }) => {
      state.documents = payload;
      state.isCompanyDocumentsLoading = false;
    });
    builder.addCase(fetchUserDocuments.fulfilled, (state, { payload }) => {
      payload.forEach((newDoc: IDocument) => {
        const iDocument = state.documents.findIndex(
          (doc) => doc.id === newDoc.id
        );

        if (iDocument !== -1) {
          state.documents[iDocument] = newDoc;
        } else {
          state.documents.push(newDoc);
        }
      });
    });
    builder.addCase(downloadDocument.fulfilled, (state, { payload }) => {});
    builder.addCase(
      createTraderootMember.fulfilled,
      (state, { payload }) => {}
    );
    builder.addCase(createBankAccount.fulfilled, (state, { payload }) => {});
    builder.addCase(
      createEmailChangeRequest.fulfilled,
      (state, { payload, meta: { arg } }) => {
        logger.debug('createEmailChangeRequest.fulfilled', arg, payload);
      }
    );
  },
});

export const {
  setPersonId,
  setActiveCompanyId,
  setRemoveUser,
  setDocumentStatus,
  setActiveBankAccountNumber,
  setIsBankAccountsLoading,
  setIsCompanyDocumentsLoading,
  setIsDocumentsLoading,
  setIsUsersLoading,
  setIsEmployeesLoading,
} = workspaceSlice.actions;

export default workspaceSlice.reducer;
